import { signUp } from 'api/auth';
import { useState } from 'react';
import {
  KoobButton,
  KoobInputLabel,
  KoobInputSegmented,
  KoobInputSelectField,
  KoobInputTextareaField,
  KoobSelectableCard,
  KoobTitle,
} from '@koob/ui';
import { camelCase, omit } from 'lodash';
import { useTranslation } from 'react-i18next';
import { SelectCurrencyField, SelectField, TextField } from 'components/Fields';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { useSnack } from 'hooks';
import logo from 'images/logo-koob.svg';
import { getCountries } from 'api/countries';
import { getCitiesByCountry } from 'api/cities';
import { useAsync } from 'react-async';
import { listSupports, phoneRegex } from 'utils';
import PhoneNumberField from 'components/Fields/PhoneNumberField';
import SelectLanguageField from 'components/Fields/SelectLanguageField';
import SelectMarketsField from 'components/Fields/SelectMarketsField';

const getOmittedValues = () => {
  return ['country', 'city', 'currencyData', 'howKnowKoobData'];
};

const Section = ({ title, children }) => (
  <section className="space-y-4">
    <KoobTitle size="text-lg">{title}</KoobTitle>
    {children}
  </section>
);

const SignUp = () => {
  const { t } = useTranslation(['auth', 'errors']);
  const { notify } = useSnack();
  const navigate = useNavigate();

  const [selectedCountry, setSelectedCountry] = useState(null);

  const { data: countriesData } = useAsync({
    promiseFn: getCountries,
  });

  const supports = (listSupports || [])
    .map(({ name: support }) => ({
      value: support,
      label: support,
    }))
    .sort((support1, support2) => support1.label.localeCompare(support2.label));

  const countries = (countriesData?.data || [])
    .map(country => ({
      value: country.id,
      label:
        country?.translations?.filter(item => item.locale === 'en')[0]?.title ||
        country?.translations?.filter(item => item.locale === 'en')[1]?.title,
    }))
    .sort((country1, country2) => country1.label.localeCompare(country2.label));

  const { data: citiesData } = useAsync({
    promiseFn: selectedCountry ? getCitiesByCountry : null,
    watch: selectedCountry,
    countryId: selectedCountry,
  });

  const cities = (citiesData?.data || [])
    .map(city => ({
      value: city.id,
      label: city?.title,
    }))
    .sort((city1, city2) => city1.label.localeCompare(city2.label));

  const handleChange = value => {
    setSelectedCountry(value);
  };

  const handleSignUp = async values => {
    const input = omit(
      {
        ...values,
        cityId: parseInt(values.city.value),
        countryId: parseInt(values?.country?.value),
        currency: values?.currencyData?.value,
        howKnowKoob: values.howKnowKoobData?.value,
        typology: values.typology,
        language: values.language?.value,
        markets: values?.markets?.map(market => {
          return {
            id: market.value,
            name: market.label,
          };
        }),
      },
      getOmittedValues(),
    );

    try {
      await signUp(input);
      notify(t('signUp.form.success'));
      return navigate('/');
    } catch (err) {
      const error = camelCase(err?.response?.data?.message ?? 'error');

      notify(t(`errors:${error}`), { type: 'error' });
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10 relative overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center blur-sm scale-105 brightness-75"
        style={{ backgroundImage: 'url(/img/auth-bg.jpg)' }}
      />

      <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg p-8 sm:p-10 w-full max-w-2xl relative z-10">
        <div className="flex justify-center mb-8">
          <img src={logo} alt="KOOB" className="h-8" />
        </div>

        <h1 className="text-xl font-bold text-k-h text-center mb-1">
          {t('signUp.title')}
        </h1>
        <p className="text-sm text-k-p text-center mb-6">
          {t('signUp.subtitle')}
        </p>

        <Formik
          initialValues={{
            firstName: '',
            lastName: '',
            jobPosition: '',
            email: '',
            companyName: '',
            phoneNumber: '',
            website: '',
            address: '',
            zipCode: '',
            city: '',
            country: '',
            currencyData: '',
            comment: '',
            howKnowKoobData: '',
            marcketplaceRequest: true,
            tripDesignerRequest: false,
            language: '',
            markets: [],
            typology: '',
          }}
          validationSchema={Yup.object().shape({
            firstName: Yup.string().required(t('errors:required')),
            lastName: Yup.string().required(t('errors:required')),
            email: Yup.string()
              .email(t('errors:email'))
              .required(t('errors:required')),
            phoneNumber: Yup.string()
              .required(t('errors:required'))
              .matches(phoneRegex, t('errors:invalidFormat')),
            city: Yup.mixed().required(t('errors:required')),
            country: Yup.mixed().required(t('errors:required')),
            address: Yup.string().required(t('errors:required')),
            zipCode: Yup.string().required(t('errors:required')),
            jobPosition: Yup.string(),
            companyName: Yup.string().required(t('errors:required')),
            website: Yup.string().url(t('errors:url')),
            marcketplaceRequest: Yup.boolean(),
            tripDesignerRequest: Yup.boolean(),
            language: Yup.object().required(t('errors:required')),
            typology: Yup.string().required(t('errors:required')),
            markets: Yup.array()
              .of(Yup.object())
              .required(t('errors:required'))
              .min(1),
          })}
          onSubmit={handleSignUp}
        >
          {({ isSubmitting, isValid, values, setFieldValue }) => (
            <Form className="space-y-8">
              <Section title={t('signUp.form.sections.contact')}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <TextField
                    label={t('signUp.form.firstName')}
                    name="firstName"
                    required
                  />
                  <TextField
                    label={t('signUp.form.lastName')}
                    name="lastName"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <TextField
                    label={t('signUp.form.email')}
                    type="email"
                    tabIndex="1"
                    name="email"
                    required
                  />
                  <PhoneNumberField
                    label={t('signUp.form.phoneNumber')}
                    name="phoneNumber"
                    required
                  />
                </div>

                <TextField
                  label={t('signUp.form.jobPosition')}
                  name="jobPosition"
                />
              </Section>

              <Section title={t('signUp.form.sections.company')}>
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1">
                    <TextField
                      label={t('signUp.form.companyName')}
                      name="companyName"
                      required
                    />
                  </div>
                  <div>
                    <KoobInputLabel label={t('signUp.form.typology')} required>
                      <KoobInputSegmented
                        options={[
                          {
                            label: t('signUp.form.organizationType.to'),
                            value: 'tour_operator',
                          },
                          {
                            label: t(
                              'signUp.form.organizationType.travelAgency',
                            ),
                            value: 'travel_agency',
                          },
                        ]}
                        value={values.typology}
                        onChange={value => setFieldValue('typology', value)}
                      />
                    </KoobInputLabel>
                  </div>
                </div>

                <TextField
                  label={t('signUp.form.website')}
                  name="website"
                  placeholder="https://www.mywebsite.com"
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <SelectLanguageField
                    name="language"
                    label={t('signUp.form.language')}
                    required
                  />
                  <SelectCurrencyField
                    label={t('signUp.form.currency')}
                    name="currencyData"
                    testId="currency"
                    searchable
                  />
                </div>

                <SelectMarketsField label={t('signUp.form.markets')} />
              </Section>

              <Section title={t('signUp.form.sections.address')}>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="sm:col-span-2">
                    <TextField
                      label={t('signUp.form.address')}
                      name="address"
                      required
                    />
                  </div>
                  <TextField
                    label={t('signUp.form.zipCode')}
                    name="zipCode"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <KoobInputSelectField
                    name="country"
                    label={t('signUp.form.country')}
                    required
                    items={countries}
                    renderOption={value => value?.label}
                    renderValue={value => value?.label}
                    onAfterChange={value => {
                      handleChange(value?.value);
                      setFieldValue('city', '');
                    }}
                  />
                  <KoobInputSelectField
                    name="city"
                    label={t('signUp.form.city')}
                    required
                    items={cities}
                    renderOption={value => value?.label}
                    renderValue={value => value?.label}
                    disabled={!selectedCountry}
                  />
                </div>
              </Section>

              <Section title={t('signUp.form.interestedIn')}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <KoobSelectableCard
                    selected
                    disabled
                    title={t('signUp.form.requestMarketplace')}
                    description={t('signUp.form.descMarketplace')}
                    data-testid="marketplace-card"
                  />
                  <KoobSelectableCard
                    selected={values.tripDesignerRequest}
                    onClick={() =>
                      setFieldValue(
                        'tripDesignerRequest',
                        !values.tripDesignerRequest,
                      )
                    }
                    title={t('signUp.form.requestTripDesigner')}
                    description={t('signUp.form.descTripDesigner')}
                    data-testid="tripdesigner-card"
                  />
                </div>
              </Section>

              <Section title={t('signUp.form.sections.additional')}>
                <KoobInputTextareaField
                  name="comment"
                  label={t('signUp.form.comment')}
                  minRows={4}
                />

                <SelectField
                  options={supports}
                  label={t('signUp.form.how_know_koob')}
                  name="howKnowKoobData"
                  renderSelectedValue={value => value?.label}
                />
              </Section>

              <KoobButton
                type="submit"
                variant="primary"
                size="lg"
                block
                loading={isSubmitting}
                tabIndex="2"
                disabled={!isValid}
                testId="send-request"
              >
                {t('signUp.form.button')}
              </KoobButton>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default SignUp;
